import { useState } from 'react';
import { Link } from 'react-router-dom';
import { dateTime, humanise, mobile as fmtMobile } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Badge, Button, Modal, Notice, SearchInput, Select } from '../components/ui';

interface AuditRow {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  createdAt: string;
  actor: { id: string; mobile: string; displayName: string | null } | null;
}

const ACTION_TONE = (action: string) =>
  action.includes('BLOCK') || action.includes('REJECT') || action.includes('DELETE') || action.includes('REFUND')
    ? 'red'
    : action.includes('APPROVE') || action.includes('ACTIVATE')
      ? 'green'
      : 'blue';

export function AuditPage() {
  const { get, set } = useListParams();
  const [open, setOpen] = useState<AuditRow | null>(null);
  const action = get('action');
  const entityType = get('entityType');

  const columns: Array<Column<AuditRow>> = [
    { key: 'when', header: 'When', render: (r) => <span className="cell-muted">{dateTime(r.createdAt)}</span> },
    {
      key: 'actor',
      header: 'Who',
      render: (r) =>
        r.actor ? (
          <Link to={`/users/${r.actor.id}`} onClick={(e) => e.stopPropagation()}>
            {r.actor.displayName ?? fmtMobile(r.actor.mobile)}
          </Link>
        ) : (
          <span className="cell-muted">system</span>
        ),
    },
    { key: 'action', header: 'Action', render: (r) => <Badge tone={ACTION_TONE(r.action)}>{humanise(r.action)}</Badge> },
    {
      key: 'entity',
      header: 'Subject',
      render: (r) => (
        <div>
          <div>{r.entityType}</div>
          <div className="cell-mono cell-clip">{r.entityId ?? '—'}</div>
        </div>
      ),
    },
    { key: 'ip', header: 'IP', render: (r) => <span className="cell-mono">{r.ip ?? '—'}</span> },
  ];

  return (
    <div className="stack">
      <Notice tone="info" title="Every admin mutation is recorded">
        Approvals, blocks, refunds, moderation and deletions each write a row here with the before and after state.
        This is what answers “who approved this KYC” a year from now.
      </Notice>

      <DataTable<AuditRow>
        queryKey={['audit', action, entityType]}
        path="/admin/audit"
        params={{ action, entityType }}
        columns={columns}
        onRowClick={setOpen}
        emptyIcon="⌚"
        emptyTitle="No admin actions recorded yet"
        toolbar={
          <>
            <SearchInput value={action} onChange={(v) => set('action', v)} placeholder="Action, e.g. KYC_APPROVE…" />
            <Select
              value={entityType}
              placeholder="Any subject"
              onChange={(e) => set('entityType', e.target.value)}
              options={['User', 'KycRecord', 'Listing', 'Payment', 'Agent', 'ChatMessage', 'ContentReport'].map((v) => ({
                value: v,
                label: v,
              }))}
            />
          </>
        }
      />

      {open && (
        <Modal
          wide
          title={humanise(open.action)}
          sub={`${open.entityType} · ${dateTime(open.createdAt)} · ${open.actor?.displayName ?? open.actor?.mobile ?? 'system'}`}
          onClose={() => setOpen(null)}
          footer={<Button onClick={() => setOpen(null)}>Close</Button>}
        >
          <div className="grid grid--2">
            <div>
              <div className="field__label" style={{ marginBottom: 6 }}>
                Before
              </div>
              <pre
                className="cell-mono"
                style={{ background: 'var(--bg)', padding: 12, borderRadius: 10, overflow: 'auto', maxHeight: 340, margin: 0 }}
              >
                {open.before ? JSON.stringify(open.before, null, 2) : '—'}
              </pre>
            </div>
            <div>
              <div className="field__label" style={{ marginBottom: 6 }}>
                After
              </div>
              <pre
                className="cell-mono"
                style={{ background: 'var(--bg)', padding: 12, borderRadius: 10, overflow: 'auto', maxHeight: 340, margin: 0 }}
              >
                {open.after ? JSON.stringify(open.after, null, 2) : '—'}
              </pre>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
